import { FrontendConstants } from '../common/FrontendConstants';
import { Settings } from '../common/Settings';
import { AuthClient } from '../exports';
import { CommonConstants } from '@shared/common';
import type { ITextDTO, ITextMessageDTO } from '@shared/common';

/** A conversation list row: the conversation plus a clamped preview of its latest message. */
export interface ITextListItemDTO extends ITextDTO {
  lastMessagePreview?: string;
}

/** Full detail for a single conversation: the conversation row plus its ordered message history (oldest first). */
export interface ITextDetailDTO {
  text: ITextDTO;
  messages: ITextMessageDTO[];
}

/** Client for the AI-texting conversation list, detail, and owner interject. Tenant-facing; routes through the lobby service dashboard surface (`dashboard/:tenantID/texts`). */
export class TextsClient {
  /** Singleton instance. */
  private static instance: TextsClient;
  /** Prevent external instantiation. */
  private constructor() {}

  /** @returns The singleton TextsClient instance. */
  public static GetInstance(): TextsClient {
    return TextsClient.instance ?? (TextsClient.instance = new TextsClient());
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/texts - List a tenant's texting conversations, newest activity first, with optional free-text search and pagination.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param offset - Number of rows to skip.
   * @param limit - Maximum number of rows.
   * @param pattern - Optional free-text search term (customer number).
   * @returns The tenant's conversations page, most recent activity first.
   */
  public async ListTexts(
    signal: AbortSignal,
    tenantID: string,
    offset: number = CommonConstants.Pagination.Offset,
    limit: number = CommonConstants.Pagination.Limit,
    pattern?: string
  ): Promise<ITextListItemDTO[]> {
    const params = new URLSearchParams({ limit: limit.toString(), offset: offset.toString() });
    if (pattern) params.append('pattern', pattern);
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/texts?${params}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch texts');
    return await res.json();
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/texts/count - Count a tenant's texting conversations matching an optional free-text search term and an optional recency floor on the last activity time.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param pattern - Optional free-text search term.
   * @param sinceISO - Optional ISO timestamp; only conversations active at or after it are counted.
   * @returns The number of matching conversations.
   */
  public async CountTexts(signal: AbortSignal, tenantID: string, pattern?: string, sinceISO?: string): Promise<number> {
    const params = new URLSearchParams();
    if (pattern) params.append('pattern', pattern);
    if (sinceISO) params.append(CommonConstants.Params.Since, sinceISO);
    const query = params.toString();
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/texts/count${query ? `?${query}` : ''}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch text count');
    return await res.json();
  }

  /**
   * GET /api/lobby/dashboard/:tenantID/texts/:textID - Fetch one conversation with its full message history.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param textID - The conversation to fetch.
   * @returns The conversation detail.
   */
  public async GetTextDetail(signal: AbortSignal, tenantID: string, textID: string): Promise<ITextDetailDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_LOBBY_URI, `dashboard/${tenantID}/texts/${textID}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch conversation');
    return await res.json();
  }

  /**
   * POST /api/lobby/dashboard/:tenantID/texts/:textID/messages - Send an owner interject into a conversation (from the tenant's AI number).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param textID - The conversation to message.
   * @param body - The message body.
   * @returns The recorded message row.
   */
  public async SendTextMessage(signal: AbortSignal, tenantID: string, textID: string, body: string): Promise<ITextMessageDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_LOBBY_URI,
      `dashboard/${tenantID}/texts/${textID}/messages`,
      { method: FrontendConstants.MethodPost, headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson }, body: JSON.stringify({ body }), signal },
      auth
    );
    if (!res.ok) throw new Error('Failed to send message');
    return await res.json();
  }
}
